/**
 * @fileoverview Encrypted, mode-aware storage for the in-browser signing
 * mnemonic ("Sign in this browser / Winbit32").
 *
 * Security model (per pre-launch review — replaces the previous plaintext
 * `localStorage` seed):
 *
 *   - SESSION mode (default): the seed lives in `sessionStorage`, so it is
 *     wiped automatically when the tab/window closes and is NEVER written to
 *     durable `localStorage`. A password is OPTIONAL here; if supplied the
 *     session copy is encrypted too (defence-in-depth against same-origin
 *     reads during the session).
 *   - PERSISTENT mode (opt-in): the seed survives across browser sessions in
 *     `localStorage`, but is ALWAYS password-encrypted. A password is
 *     mandatory — we refuse to write a persistent record without one.
 *
 * Invariant enforced here: the *plaintext* mnemonic is never written to
 * `localStorage` under any mode. Only `sessionStorage` may hold a plaintext
 * (session-scoped) copy, and only when the user declined a password.
 *
 * Crypto: PBKDF2-SHA-256 (high iteration count) → AES-GCM-256 via the Web
 * Crypto API. This deliberately mirrors WINBIT32's `.wult` vault wrapper so
 * the password handling is consistent across our apps.
 */

export type MnemonicStorageMode = 'session' | 'persistent';

export interface StoredMnemonicInfo {
	/** A stored record (encrypted or session-plaintext) exists. */
	exists: boolean;
	/** Where the record lives, or null when none. */
	mode: MnemonicStorageMode | null;
	/** True when unlocking requires a password. */
	encrypted: boolean;
}

export interface SaveMnemonicOptions {
	mode: MnemonicStorageMode;
	/** Required for `persistent`; optional for `session`. */
	password?: string;
}

interface EncryptedRecord {
	v: 1;
	mode: MnemonicStorageMode;
	enc: true;
	kdf: 'PBKDF2-SHA256';
	iters: number;
	salt: string; // base64
	iv: string; // base64
	ct: string; // base64
}

interface PlainRecord {
	v: 1;
	mode: 'session';
	enc: false;
	m: string; // mnemonic — only ever written to sessionStorage
}

type MnemonicRecord = EncryptedRecord | PlainRecord;

// Storage keys. The v1 record key is distinct from the legacy plaintext key so
// we can detect + migrate old installs (see migrateLegacyPlaintext).
const VAULT_KEY = 'winbit32_wallet_vault_v1';
const LEGACY_PLAINTEXT_KEY = 'winbit32_wallet_mnemonic';

// OWASP-recommended order of magnitude for PBKDF2-SHA256 (2023+). Matches the
// WINBIT32 `.wult` wrapper so a seed can be moved between the apps mentally
// with the same password expectations.
const PBKDF2_ITERS = 600000;
const SALT_BYTES = 16;
const IV_BYTES = 12;

const hasWindow = (): boolean => typeof window !== 'undefined';

const getCrypto = (): Crypto => {
	const c = hasWindow() ? window.crypto : (globalThis as any).crypto;
	if (!c || !c.subtle) {
		throw new Error('Web Crypto API unavailable — a secure (HTTPS) context is required to encrypt your wallet.');
	}
	return c;
};

const toBase64 = (bytes: Uint8Array): string => {
	let binary = '';
	for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
	return btoa(binary);
};

const fromBase64 = (b64: string): Uint8Array => {
	const binary = atob(b64);
	const out = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
	return out;
};

const deriveKey = async (password: string, salt: Uint8Array, iters: number): Promise<CryptoKey> => {
	const cryptoObj = getCrypto();
	const baseKey = await cryptoObj.subtle.importKey(
		'raw',
		new TextEncoder().encode(password),
		'PBKDF2',
		false,
		['deriveKey']
	);
	return cryptoObj.subtle.deriveKey(
		{ name: 'PBKDF2', salt, iterations: iters, hash: 'SHA-256' },
		baseKey,
		{ name: 'AES-GCM', length: 256 },
		false,
		['encrypt', 'decrypt']
	);
};

const encryptMnemonic = async (
	mnemonic: string,
	password: string,
	mode: MnemonicStorageMode
): Promise<EncryptedRecord> => {
	const cryptoObj = getCrypto();
	const salt = cryptoObj.getRandomValues(new Uint8Array(SALT_BYTES));
	const iv = cryptoObj.getRandomValues(new Uint8Array(IV_BYTES));
	const key = await deriveKey(password, salt, PBKDF2_ITERS);
	const ct = await cryptoObj.subtle.encrypt(
		{ name: 'AES-GCM', iv },
		key,
		new TextEncoder().encode(mnemonic)
	);
	return {
		v: 1,
		mode,
		enc: true,
		kdf: 'PBKDF2-SHA256',
		iters: PBKDF2_ITERS,
		salt: toBase64(salt),
		iv: toBase64(iv),
		ct: toBase64(new Uint8Array(ct))
	};
};

const decryptRecord = async (record: EncryptedRecord, password: string): Promise<string> => {
	const cryptoObj = getCrypto();
	const key = await deriveKey(password, fromBase64(record.salt), record.iters || PBKDF2_ITERS);
	try {
		const pt = await cryptoObj.subtle.decrypt(
			{ name: 'AES-GCM', iv: fromBase64(record.iv) },
			key,
			fromBase64(record.ct)
		);
		return new TextDecoder().decode(pt);
	} catch {
		// AES-GCM authentication failure ⇒ wrong password (or tampered blob).
		throw new Error('Incorrect password.');
	}
};

const safeGet = (storage: Storage | undefined, key: string): string | null => {
	try {
		return storage?.getItem(key) ?? null;
	} catch {
		return null;
	}
};

const safeRemove = (storage: Storage | undefined, key: string): void => {
	try {
		storage?.removeItem(key);
	} catch {
		/* private mode / quota — best effort */
	}
};

const parseRecord = (raw: string | null): MnemonicRecord | null => {
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw) as MnemonicRecord;
		if (parsed && parsed.v === 1 && (parsed.mode === 'session' || parsed.mode === 'persistent')) {
			return parsed;
		}
	} catch {
		/* not a v1 record */
	}
	return null;
};

/**
 * Read the current record, preferring the session copy over a persistent one
 * (an unlocked session record means the user is actively using this tab).
 */
const readRecord = (): MnemonicRecord | null => {
	if (!hasWindow()) return null;
	const sessionRec = parseRecord(safeGet(window.sessionStorage, VAULT_KEY));
	if (sessionRec) return sessionRec;
	return parseRecord(safeGet(window.localStorage, VAULT_KEY));
};

/**
 * Migrate a legacy plaintext `winbit32_wallet_mnemonic` localStorage entry to
 * a session record and delete the plaintext immediately. Returns the recovered
 * mnemonic (so the caller can keep the user signed in for this session) or
 * null if there was nothing to migrate.
 *
 * This runs opportunistically on first access so existing installs stop
 * leaking a durable plaintext seed the moment the new build loads.
 */
export const migrateLegacyPlaintext = (): string | null => {
	if (!hasWindow()) return null;
	const legacy = safeGet(window.localStorage, LEGACY_PLAINTEXT_KEY);
	// Never clobber an already-migrated v1 record.
	if (!legacy || readRecord()) {
		if (legacy) safeRemove(window.localStorage, LEGACY_PLAINTEXT_KEY);
		return null;
	}
	const normalised = legacy.trim().replace(/\s+/g, ' ');
	const record: PlainRecord = { v: 1, mode: 'session', enc: false, m: normalised };
	try {
		window.sessionStorage.setItem(VAULT_KEY, JSON.stringify(record));
	} catch {
		/* sessionStorage unavailable — still proceed to delete the plaintext */
	}
	safeRemove(window.localStorage, LEGACY_PLAINTEXT_KEY);
	return normalised || null;
};

/**
 * Persist the mnemonic according to the chosen mode. Enforces the security
 * invariants:
 *   - persistent ⇒ a password is required (always encrypted, localStorage).
 *   - session    ⇒ password optional; sessionStorage only.
 */
export const saveMnemonic = async (mnemonic: string, options: SaveMnemonicOptions): Promise<void> => {
	if (!hasWindow()) return;
	const normalised = (mnemonic || '').trim().replace(/\s+/g, ' ');
	if (!normalised) throw new Error('Mnemonic is required');

	const { mode } = options;
	const password = options.password?.length ? options.password : undefined;

	if (mode === 'persistent' && !password) {
		throw new Error('A password is required to remember your wallet on this device.');
	}

	// Always start clean so a mode change can't leave a stale copy behind.
	clearMnemonic();

	let record: MnemonicRecord;
	if (password) {
		record = await encryptMnemonic(normalised, password, mode);
	} else {
		record = { v: 1, mode: 'session', enc: false, m: normalised };
	}

	const storage = mode === 'persistent' ? window.localStorage : window.sessionStorage;
	try {
		storage.setItem(VAULT_KEY, JSON.stringify(record));
	} catch (err) {
		throw new Error('Could not save your wallet (browser storage is full or disabled).');
	}
};

/**
 * Return the mnemonic if it is available WITHOUT a password (i.e. a
 * session-mode plaintext record), else null. Encrypted records require
 * `unlockMnemonic`.
 */
export const loadUnprotectedMnemonic = (): string | null => {
	const record = readRecord();
	if (record && record.enc === false) return record.m || null;
	return null;
};

/** Decrypt and return the stored mnemonic using the supplied password. */
export const unlockMnemonic = async (password: string): Promise<string> => {
	const record = readRecord();
	if (!record) throw new Error('No stored wallet to unlock.');
	if (record.enc === false) return record.m;
	return decryptRecord(record, password);
};

/** Describe what's stored, for UI gating (enter-seed vs unlock vs auto-connect). */
export const getStoredMnemonicInfo = (): StoredMnemonicInfo => {
	const record = readRecord();
	if (!record) {
		// A not-yet-migrated legacy plaintext seed still counts as "exists".
		const legacy = hasWindow() ? safeGet(window.localStorage, LEGACY_PLAINTEXT_KEY) : null;
		if (legacy) return { exists: true, mode: 'session', encrypted: false };
		return { exists: false, mode: null, encrypted: false };
	}
	return { exists: true, mode: record.mode, encrypted: record.enc === true };
};

/** Remove every stored copy (session + persistent + legacy plaintext). */
export const clearMnemonic = (): void => {
	if (!hasWindow()) return;
	safeRemove(window.sessionStorage, VAULT_KEY);
	safeRemove(window.localStorage, VAULT_KEY);
	safeRemove(window.localStorage, LEGACY_PLAINTEXT_KEY);
};
